import { useState, useMemo } from 'react';
import { Button, Input, Badge } from '@tkpod/ui';
import type { MetadataFieldDef, MetadataFieldType, OptionUIType } from '@tkpod/shared';
import { getCompatibleFieldTypes, getDefaultFieldKeys } from './default-metadata-schema';

const ALL_FIELD_TYPES: { value: MetadataFieldType; label: string }[] = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'select', label: 'Select / Dropdown' },
  { value: 'color', label: 'Color Picker' },
  { value: 'image', label: 'Image Upload' },
];

function labelToCamelCase(label: string): string {
  return label
    .replace(/[^a-zA-Z0-9\s]/g, '')
    .trim()
    .split(/\s+/)
    .map((word, i) =>
      i === 0
        ? word.toLowerCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join('');
}

interface MetadataSchemaEditorProps {
  schema: MetadataFieldDef[];
  onChange: (schema: MetadataFieldDef[]) => void;
  uiType: OptionUIType;
  disabled?: boolean;
}

export function MetadataSchemaEditor({ schema, onChange, uiType, disabled }: MetadataSchemaEditorProps) {
  const [editingField, setEditingField] = useState<{ mode: 'add' } | { mode: 'edit'; idx: number } | null>(null);
  const lockedKeys = useMemo(() => new Set(getDefaultFieldKeys(uiType)), [uiType]);

  const handleRemove = (idx: number) => {
    if (lockedKeys.has(schema[idx]?.key)) return;
    onChange(schema.filter((_, i) => i !== idx));
  };

  const handleMoveUp = (idx: number) => {
    if (idx === 0) return;
    const copy = [...schema];
    [copy[idx - 1], copy[idx]] = [copy[idx], copy[idx - 1]];
    onChange(copy);
  };

  const handleMoveDown = (idx: number) => {
    if (idx >= schema.length - 1) return;
    const copy = [...schema];
    [copy[idx], copy[idx + 1]] = [copy[idx + 1], copy[idx]];
    onChange(copy);
  };

  const handleToggleShowInTable = (idx: number) => {
    const copy = [...schema];
    copy[idx] = { ...copy[idx], showInTable: !copy[idx].showInTable };
    onChange(copy);
  };

  const handleSaveField = (field: MetadataFieldDef) => {
    if (editingField?.mode === 'edit') {
      const copy = [...schema];
      copy[editingField.idx] = field;
      onChange(copy);
    } else {
      onChange([...schema, field]);
    }
    setEditingField(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-text-primary">
          Metadata Schema ({schema.length} field{schema.length !== 1 ? 's' : ''})
        </h4>
        <Button
          size="sm"
          onClick={() => setEditingField({ mode: 'add' })}
          disabled={disabled || editingField !== null}
        >
          + Add Field
        </Button>
      </div>

      {schema.length === 0 && !editingField && (
        <div className="rounded-lg border border-dashed border-border p-4 text-center">
          <p className="text-sm text-text-tertiary">
            No metadata fields defined. Click &quot;+ Add Field&quot; to add custom fields.
          </p>
        </div>
      )}

      {schema.length > 0 && (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-secondary/50 text-left text-text-secondary">
                <th className="px-3 py-2 font-medium">Label</th>
                <th className="px-3 py-2 font-medium">Key</th>
                <th className="px-3 py-2 font-medium">Type</th>
                <th className="px-3 py-2 font-medium">Req</th>
                <th className="px-3 py-2 font-medium">Table</th>
                <th className="px-3 py-2 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {schema.map((field, idx) => {
                const isLocked = lockedKeys.has(field.key);
                return (
                  <tr key={`${field.key}-${idx}`} className="border-b border-border last:border-0 hover:bg-surface-secondary/30">
                    <td className="px-3 py-2 text-text-primary">
                      {field.label}
                      {isLocked && (
                        <span className="ml-1.5 inline-block text-[10px] text-text-tertiary" title="Default field — cannot be removed">
                          🔒
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs text-text-secondary">{field.key}</td>
                    <td className="px-3 py-2">
                      <Badge variant="default" size="sm">{field.type}</Badge>
                    </td>
                    <td className="px-3 py-2">
                      {field.required ? (
                        <span className="text-xs font-medium text-warning-600">Yes</span>
                      ) : (
                        <span className="text-xs text-text-tertiary">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => !disabled && handleToggleShowInTable(idx)}
                        disabled={disabled}
                        className={`text-xs font-medium ${
                          field.showInTable ? 'text-success' : 'text-text-tertiary'
                        }`}
                      >
                        {field.showInTable ? 'Visible' : 'Hidden'}
                      </button>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleMoveUp(idx)}
                          disabled={disabled || idx === 0}
                          className="rounded p-1 text-text-tertiary hover:bg-surface-secondary disabled:opacity-30"
                          title="Move up"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => handleMoveDown(idx)}
                          disabled={disabled || idx >= schema.length - 1}
                          className="rounded p-1 text-text-tertiary hover:bg-surface-secondary disabled:opacity-30"
                          title="Move down"
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingField({ mode: 'edit', idx })}
                          disabled={disabled || editingField !== null}
                          className="rounded px-1.5 py-0.5 text-xs text-brand-600 hover:bg-brand-50 disabled:opacity-30"
                        >
                          Edit
                        </button>
                        {!isLocked && (
                          <button
                            type="button"
                            onClick={() => handleRemove(idx)}
                            disabled={disabled}
                            className="rounded px-1.5 py-0.5 text-xs text-danger-600 hover:bg-danger-50 disabled:opacity-30"
                          >
                            ×
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {editingField && (
        <FieldEditor
          initial={editingField.mode === 'edit' && schema[editingField.idx] ? schema[editingField.idx] : undefined}
          isLocked={editingField.mode === 'edit' && schema[editingField.idx] ? lockedKeys.has(schema[editingField.idx].key) : false}
          existingKeys={
            schema
              .map((f) => f.key)
              .filter((_, i) => (editingField.mode === 'edit' ? i !== editingField.idx : true))
          }
          uiType={uiType}
          onSave={handleSaveField}
          onCancel={() => setEditingField(null)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field Editor (inline form below the table)
// ---------------------------------------------------------------------------

function FieldEditor({
  initial,
  isLocked,
  existingKeys,
  uiType,
  onSave,
  onCancel,
}: {
  initial?: MetadataFieldDef;
  isLocked: boolean;
  existingKeys: string[];
  uiType: OptionUIType;
  onSave: (field: MetadataFieldDef) => void;
  onCancel: () => void;
}) {
  const compatibleTypes = useMemo(() => getCompatibleFieldTypes(uiType), [uiType]);
  const fieldTypes = useMemo(
    () => ALL_FIELD_TYPES.filter((ft) => compatibleTypes.includes(ft.value)),
    [compatibleTypes],
  );

  const [label, setLabel] = useState(initial?.label ?? '');
  const [type, setType] = useState<MetadataFieldType>(
    initial?.type && compatibleTypes.includes(initial.type) ? initial.type : compatibleTypes[0],
  );
  const [required, setRequired] = useState(initial?.required ?? false);
  const [showInTable, setShowInTable] = useState(initial?.showInTable ?? true);
  const [placeholder, setPlaceholder] = useState(initial?.placeholder ?? '');
  const [optionsText, setOptionsText] = useState((initial?.options ?? []).join(', '));
  const [group, setGroup] = useState(initial?.group ?? '');
  const [error, setError] = useState<string | null>(null);

  const generatedKey = initial?.key ?? labelToCamelCase(label);

  const handleSave = () => {
    if (!label.trim()) {
      setError('Label is required.');
      return;
    }

    const key = initial?.key ?? labelToCamelCase(label.trim());
    if (!key) {
      setError('Could not generate a valid key from the label. Use only letters and numbers.');
      return;
    }

    if (!initial && existingKeys.includes(key)) {
      setError(`A field with key "${key}" already exists. Choose a different label.`);
      return;
    }

    const field: MetadataFieldDef = {
      key,
      label: label.trim(),
      type,
      showInTable,
    };

    if (required) field.required = true;
    if (placeholder.trim()) field.placeholder = placeholder.trim();
    if (group.trim()) field.group = group.trim();

    if (type === 'select') {
      field.options = optionsText
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      if (field.options.length === 0) {
        setError('Select fields need at least one option.');
        return;
      }
    }

    onSave(field);
  };

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50/30 p-4 space-y-3">
      <h5 className="text-sm font-semibold text-text-primary">
        {initial ? 'Edit Field' : 'New Field'}
        {isLocked && <span className="ml-2 text-xs font-normal text-text-tertiary">(default field — key cannot be changed)</span>}
      </h5>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Label *</label>
          <Input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="e.g. Hex Code"
            className="text-sm"
            disabled={isLocked}
          />
          {!initial && label.trim() && (
            <p className="mt-0.5 text-[11px] text-text-tertiary">
              Key: <code className="font-mono">{generatedKey || '—'}</code>
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Type</label>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as MetadataFieldType)}
            disabled={isLocked}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:opacity-50"
          >
            {fieldTypes.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-text-secondary">Group</label>
          <Input
            value={group}
            onChange={(e) => setGroup(e.target.value)}
            placeholder="Optional grouping"
            className="text-sm"
          />
        </div>
        {type !== 'boolean' && type !== 'image' && (
          <div>
            <label className="mb-1 block text-xs font-medium text-text-secondary">Placeholder</label>
            <Input
              value={placeholder}
              onChange={(e) => setPlaceholder(e.target.value)}
              placeholder="Input placeholder"
              className="text-sm"
            />
          </div>
        )}
        {type === 'select' && (
          <div className="col-span-2">
            <label className="mb-1 block text-xs font-medium text-text-secondary">Options (comma-separated) *</label>
            <Input
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              placeholder="e.g. BABY, YOUTH, ADULT"
              className="text-sm"
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={required} onChange={(e) => setRequired(e.target.checked)} className="rounded" />
          Required
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={showInTable} onChange={(e) => setShowInTable(e.target.checked)} className="rounded" />
          Show in table
        </label>
      </div>

      {error && <p className="text-xs text-danger-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={onCancel}>Cancel</Button>
        <Button size="sm" onClick={handleSave}>
          {initial ? 'Update Field' : 'Add Field'}
        </Button>
      </div>
    </div>
  );
}
